/**
 * A lead climber's time: seconds everywhere, with the **thousandths** in the
 * decimals, and the three boxes a judge reads it off.
 *
 * Three digits rather than two because the time is the only thing that separates
 * two climbers on the same hold, and because the clock that measures it counts in
 * milliseconds anyway — the column is `decimal(9,3)` to match.
 *
 * Apart from `LeadTimeInput` so the lint's `react-refresh/only-export-components`
 * rule gets a component file that exports components only.
 */

/** A time split the way it is typed: minutes, seconds, thousandths. */
export interface TimeParts {
  minutes: string;
  seconds: string;
  thousandths: string;
}

const EMPTY: TimeParts = { minutes: '', seconds: '', thousandths: '' };

/** The largest each box may hold. */
export const TIME_PART_MAX: Record<keyof TimeParts, number> = {
  minutes: 59,
  seconds: 59,
  thousandths: 999,
};

/** How many digits each box takes, and is read back padded to. */
export const TIME_PART_DIGITS: Record<keyof TimeParts, number> = {
  minutes: 2,
  seconds: 2,
  thousandths: 3,
};

/**
 * Seconds — the unit everything downstream uses — split into the three boxes.
 * Null is an empty field, not zero.
 */
export function toParts(seconds: number | null | undefined): TimeParts {
  if (seconds == null || Number.isNaN(seconds)) return EMPTY;

  const total = Math.max(0, Math.round(seconds * 1000));

  return {
    minutes: String(Math.floor(total / 60_000)),
    seconds: String(Math.floor((total % 60_000) / 1000)).padStart(2, '0'),
    thousandths: String(total % 1000).padStart(3, '0'),
  };
}

/**
 * And back. A field left blank counts as zero, so `:30` is thirty seconds — but a
 * time with nothing in it at all is nothing, which is what keeps an untouched row
 * from saving as 0.000.
 */
export function toSeconds(parts: TimeParts): number | null {
  const { minutes, seconds, thousandths } = parts;
  if (minutes === '' && seconds === '' && thousandths === '') return null;

  const total =
    Number(minutes || 0) * 60_000 + Number(seconds || 0) * 1000 + Number(thousandths || 0);

  return total / 1000;
}

/**
 * One box as it was typed, kept to its own digits and its own range.
 *
 * Reads the digits as a **number**, which is what makes a box with a leading zero
 * editable at all: a seconds box reading back as `05` hands us `057` when a 7 is
 * typed into it, and taking the *first* two digits of that gave back the 05 it
 * started with — the "I cannot edit this field" of it. Cleared stays cleared,
 * because an empty box is not a zero.
 */
export function parsePart(key: keyof TimeParts, typed: string): string {
  const digits = typed.replace(/\D/g, '').slice(0, TIME_PART_DIGITS[key]);
  if (digits === '') return '';

  return String(Math.min(Number(digits), TIME_PART_MAX[key]));
}

/**
 * What a box shows the moment it is typed in: its value with the padding taken
 * off, so there is no leading zero in front of the caret. `05` is `5`, `000` is
 * `0`, and `500` is left alone.
 */
export function unpaddedPart(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits === '') return '';

  return String(Number(digits));
}

/** `2:05.500` over a minute, `45.234s` under one — the way a results sheet reads. */
export function formatLeadTime(seconds: number | null | undefined): string {
  if (seconds == null) return '—';

  const { minutes, seconds: secs, thousandths } = toParts(seconds);
  return Number(minutes) > 0
    ? `${minutes}:${secs}.${thousandths}`
    : `${Number(secs)}.${thousandths}s`;
}
